import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Badge, Pagination, Modal, Form, Row, Col } from 'react-bootstrap';
import { fetchActivitiesRequest, fetchActivityByIdRequest, updateActivityRequest, deleteActivityRequest } from '../../store/actions/dataActions';
import { getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import PartnerCompanyAutocomplete from '../../components/Common/PartnerCompanyAutocomplete';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import { FiCalendar, FiRefreshCw, FiEye, FiEdit, FiPlus, FiTrash2 } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminActivities.css';

// Helper function to get nested values
const getDeep = (obj, path, defaultValue = null) => {
    const keys = Array.isArray(path) ? path : path.split('.');
    let result = obj;
    for (const key of keys) {
        if (result == null || typeof result !== 'object') return defaultValue;
        result = result[key];
    }
    return result !== undefined && result !== null ? result : defaultValue;
};

// Fonction pour convertir une date au format "YYYY-MM-DD HH:mm:ss" ou "YYYY-MM-DD" en "Jun 18, 2024 2:00 pm"
const convertToApiDateFormat = (dateStr, timeStr = '') => {
    if (!dateStr || dateStr === '') return '';

    try {
        let date;
        let time = timeStr || '10:00:00';

        // Si la date est au format YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            date = new Date(dateStr);
        } else {
            // Essayer de parser la date
            date = new Date(dateStr);
        }

        if (isNaN(date.getTime())) return '';

        // Extraire les heures et minutes du timeStr
        const timeParts = time.split(':');
        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1] || '0', 10);

        date.setHours(hours, minutes, 0);

        // Formater en "Jun 18, 2024 2:00 pm"
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();

        let hour12 = date.getHours();
        const ampm = hour12 >= 12 ? 'pm' : 'am';
        hour12 = hour12 % 12;
        hour12 = hour12 ? hour12 : 12; // 0 devrait être 12
        const minutesStr = date.getMinutes().toString().padStart(2, '0');

        return `${month.toUpperCase()} ${day.toString().padStart(2, '0')}, ${year} ${hour12}:${minutesStr} ${ampm}`;
    } catch (error) {
        return '';
    }
};

// Fonction pour extraire la date et l'heure d'une chaîne au format "Jun 18, 2024 2:00 pm" ou "YYYY-MM-DD"
const parseApiDate = (dateStr) => {
    if (!dateStr || dateStr === '') return { date: '', time: '' };

    try {
        // Si c'est déjà au format YYYY-MM-DD, retourner tel quel avec une heure par défaut
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            return {
                date: dateStr,
                time: '10:00'
            };
        }

        // Parser le format "Jun 18, 2024 2:00 pm" ou "JUN 18, 2024 2:00 pm"
        const months = {
            'jan': '01', 'feb': '02', 'mar': '03', 'apr': '04', 'may': '05', 'jun': '06',
            'jul': '07', 'aug': '08', 'sep': '09', 'oct': '10', 'nov': '11', 'dec': '12'
        };

        // Pattern plus flexible pour gérer les majuscules/minuscules
        const parts = dateStr.match(/(\w+)\s+(\d+),\s+(\d+)\s+(\d+):(\d+)\s+(am|pm)/i);
        if (parts) {
            const monthKey = parts[1].toLowerCase();
            const month = months[monthKey] || '01';
            const day = parts[2].padStart(2, '0');
            const year = parts[3];
            let hour = parseInt(parts[4], 10);
            const minutes = parts[5];
            const ampm = parts[6].toLowerCase();

            if (ampm === 'pm' && hour !== 12) hour += 12;
            if (ampm === 'am' && hour === 12) hour = 0;

            return {
                date: `${year}-${month}-${day}`,
                time: `${hour.toString().padStart(2, '0')}:${minutes}`
            };
        }
    } catch (error) {
        // Ignore
    }

    return { date: '', time: '' };
};

// Fonction pour convertir une date du format "DD/MM/YYYY" en "YYYY-MM-DD" pour les inputs date
const convertDateToInputFormat = (dateStr) => {
    if (!dateStr || dateStr === '') return '';

    try {
        // Format attendu: "01/01/2025" (DD/MM/YYYY)
        const parts = dateStr.split('/');
        if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            return `${year}-${month}-${day}`;
        }
    } catch (error) {
        console.error('Erreur lors de la conversion de la date:', error);
    }

    return '';
};

const formatActivityTime = (timeStr) => {
    if (timeStr === null || timeStr === undefined || timeStr === '') return 'N/A';
    return String(timeStr).substring(0, 5);
};

const EMPTY_ACTIVITY_FILTERS = {
    category: '',
    name: '',
    activity_start_date_from: '',
    activity_start_date_to: '',
    activity_end_date_from: '',
    activity_end_date_to: '',
    company_name: '',
    activity_code: '',
    reservation_number: '',
    client_first_name: '',
    client_last_name: '',
    client_email: '',
};

const ACTIVITY_FILTER_KEYS = Object.keys(EMPTY_ACTIVITY_FILTERS);

const AdminActivities = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { activities, selectedActivity, updateActivity, deleteActivity } = useSelector((state) => state.data);
    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        companyPartnerSearch,
        setCompanyPartnerSearch,
        clearSearchState,
    } = usePersistedSearchState('activities', EMPTY_ACTIVITY_FILTERS, {
        withCompanyPartnerSearch: true,
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedActivityId, setSelectedActivityId] = useState(null);
    const [activityToDelete, setActivityToDelete] = useState(null);
    const [filterPartners, setFilterPartners] = useState([]);
    const [filterPartnersLoading, setFilterPartnersLoading] = useState(false);
    const [filterPartnersError, setFilterPartnersError] = useState(null);

    const partnerCompanyOptions = useMemo(() => {
        const names = filterPartners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [filterPartners]);

    const [formData, setFormData] = useState({
        category: '',
        name: '',
        activity_address: '',
        zip_code: '',
        city: '',
        country: '',
        activity_start_date: '',
        activity_start_time: '',
        activity_end_date: '',
        activity_end_time: '',
        activity_price: '',
        reservation_number: '',
        supplier_name: '',
        supplier_address: '',
        supplier_zip_code: '',
        supplier_city: '',
        supplier_country: '',
    });

    const buildActivitiesRequestParams = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };
        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        ACTIVITY_FILTER_KEYS.forEach((key) => {
            const value = activeFilters[key];
            if (typeof value === 'string' && value.trim() !== '') {
                params[key] = value.trim();
            }
        });

        return params;
    }, [itemsPerPage, appliedFilters]);

    // Charger les activités avec pagination et filtres
    const loadActivities = useCallback((page = 1, filtersToUse = null) => {
        dispatch(fetchActivitiesRequest(buildActivitiesRequestParams(page, filtersToUse)));
    }, [dispatch, buildActivitiesRequestParams]);

    useEffect(() => {
        loadActivities(currentPage);
    }, [currentPage, loadActivities]);

    useEffect(() => {
        const loadFilterPartners = async () => {
            try {
                setFilterPartnersLoading(true);
                setFilterPartnersError(null);
                const response = await getAllPartners();
                const partnersArray = response?.users && Array.isArray(response.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                setFilterPartners(partnersArray);
            } catch (error) {
                setFilterPartners([]);
                setFilterPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            } finally {
                setFilterPartnersLoading(false);
            }
        };

        loadFilterPartners();
    }, []);

    useEffect(() => {
        if (updateActivity.success) {
            setShowEditModal(false);
            setSelectedActivityId(null);
            loadActivities(currentPage);
        }
    }, [updateActivity.success, currentPage, loadActivities]);

    useEffect(() => {
        if (deleteActivity.success) {
            setShowDeleteModal(false);
            setActivityToDelete(null);
            loadActivities(currentPage);
        }
    }, [deleteActivity.success, currentPage, loadActivities]);


    const handleRefresh = () => {
        loadActivities(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        const companyName = (filters.company_name || companyPartnerSearch).trim();
        const nextFilters = {
            ...EMPTY_ACTIVITY_FILTERS,
            ...filters,
            company_name: companyName,
        };
        setFilters(nextFilters);
        setCompanyPartnerSearch(companyName);
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
        loadActivities(1, nextFilters);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_ACTIVITY_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadActivities(1, emptyFilters);
        }, 100);
    };

    const paginatedResponse = activities?.data;
    const activitiesArray = Array.isArray(paginatedResponse?.data) ? paginatedResponse.data : [];
    const meta = paginatedResponse?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    };

    // Pagination côté serveur - utiliser les métadonnées de l'API
    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + activitiesArray.length, totalItems);

    // Utiliser directement les activités de l'API (pas de filtrage côté client)
    const currentActivities = activitiesArray;

    // Calculate pagination range (show max 5 pages around current)
    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };


    const handleDetails = (activityId) => {
        if (activityId) {
            navigate(`/activity/activities/${activityId}`);
        }
    };

    const handleEdit = (activityId) => {
        setSelectedActivityId(activityId);
        dispatch(fetchActivityByIdRequest(activityId));
        setShowEditModal(true);
    };

    const handleOpenDeleteModal = (activity) => {
        setActivityToDelete(activity);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setActivityToDelete(null);
    };

    const handleConfirmDelete = () => {
        if (activityToDelete?.activity_id) {
            dispatch(deleteActivityRequest(activityToDelete.activity_id));
        }
    };

    useEffect(() => {
        if (selectedActivityId && selectedActivity?.data && showEditModal) {
            const activity = selectedActivity?.data?.activity || selectedActivity?.data || null;
            if (activity) {
                // Récupérer les dates et heures
                const startDate = getDeep(activity, 'activity_start_date', '');
                const startTime = getDeep(activity, 'activity_start_time', '');
                const endDate = getDeep(activity, 'activity_end_date', '');
                const endTime = getDeep(activity, 'activity_end_time', '');

                // Si les dates sont au format "Jun 18, 2024 2:00 pm", les parser
                const startDateTime = startDate.includes(',') ? parseApiDate(startDate) : { date: startDate, time: startTime || '10:00:00' };
                const endDateTime = endDate.includes(',') ? parseApiDate(endDate) : { date: endDate, time: endTime || '12:00:00' };

                // Extraire juste l'heure (HH:mm) du time si c'est au format HH:mm:ss
                const formatTime = (timeStr) => {
                    if (!timeStr) return '';
                    return timeStr.substring(0, 5); // Prendre HH:mm
                };

                setFormData({
                    category: getDeep(activity, 'category', ''),
                    name: getDeep(activity, 'name', ''),
                    activity_address: getDeep(activity, 'activity_address', ''),
                    zip_code: getDeep(activity, 'activity_zip_code', ''),
                    city: getDeep(activity, 'activity_city', ''),
                    country: getDeep(activity, 'activity_country', ''),
                    activity_start_date: startDateTime.date,
                    activity_start_time: formatTime(startDateTime.time || startTime),
                    activity_end_date: endDateTime.date,
                    activity_end_time: formatTime(endDateTime.time || endTime),
                    activity_price: String(getDeep(activity, 'activity_price', '') || '').replace(' EUR', '').replace(',', '.').replace(/[^0-9.]/g, ''),
                    reservation_number: getDeep(activity, 'reservation_number', ''),
                    supplier_name: getDeep(activity, 'supplier_name', ''),
                    supplier_address: getDeep(activity, 'supplier_address', ''),
                    supplier_zip_code: getDeep(activity, 'supplier_zip_code', ''),
                    supplier_city: getDeep(activity, 'supplier_city', ''),
                    supplier_country: getDeep(activity, 'supplier_country', ''),
                });
            }
        }
    }, [selectedActivityId, selectedActivity, showEditModal]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmitEdit = (e) => {
        e.preventDefault();

        // Construire combined_addres
        const combinedAddress = `${formData.activity_address || ''},${formData.zip_code || ''},${formData.city || ''},${formData.country || ''}`.replace(/^,|,$/g, '').replace(/,,+/g, ',');

        // Convertir les dates
        const activityStartDate = convertToApiDateFormat(formData.activity_start_date, formData.activity_start_time);
        const activityEndDate = convertToApiDateFormat(formData.activity_end_date, formData.activity_end_time);

        const dataToSend = {
            category: formData.category,
            name: formData.name || null,
            activity_address: formData.activity_address,
            zip_code: formData.zip_code || null,
            city: formData.city,
            country: formData.country,
            combined_addres: combinedAddress,
            activity_start_date: activityStartDate,
            activity_end_date: activityEndDate,
            activity_price: formData.activity_price,
            reservation_number: formData.reservation_number || null,
            supplier_name: formData.supplier_name || null,
            supplier_address: formData.supplier_address || null,
            supplier_zip_code: formData.supplier_zip_code || null,
            supplier_city: formData.supplier_city || null,
            supplier_country: formData.supplier_country || null,
        };

        dispatch(updateActivityRequest(selectedActivityId, dataToSend));
    };


    return (
        <div className="dashboard-page admin-activities-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiCalendar className="me-2" />
                                Gestion Activities
                            </h2>
                            <p className="text-muted mb-0">Gérez toutes les activités</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" onClick={handleRefresh} disabled={activities.loading}>
                                <FiRefreshCw className={activities.loading ? 'spinning' : ''} /> Actualiser
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => navigate('/activity/activities/create')}
                            >
                                <FiPlus /> Ajouter une activité
                            </Button>
                        </div>
                    </div>

                    {activities.error && (
                        <Alert variant="danger" className="mt-3">
                            {activities.error}
                        </Alert>
                    )}
                    {updateActivity.success && (
                        <Alert variant="success" className="mt-3">
                            Activité mise à jour avec succès.
                        </Alert>
                    )}
                    {updateActivity.error && (
                        <Alert variant="danger" className="mt-3">
                            {updateActivity.error}
                        </Alert>
                    )}
                    {deleteActivity.success && (
                        <Alert variant="success" className="mt-3">
                            Activité supprimée avec succès.
                        </Alert>
                    )}
                    {deleteActivity.error && (
                        <Alert variant="danger" className="mt-3">
                            {deleteActivity.error}
                        </Alert>
                    )}

                    {/* Filtres avancés */}
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Catégorie</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="category"
                                            value={filters.category}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: Event, Location de bateau"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={filters.name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Nom de l'activité"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code activité</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="activity_code"
                                            value={filters.activity_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: act_123456"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Numéro de réservation"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Nom de société</Form.Label>
                                    <PartnerCompanyAutocomplete
                                        value={filters.company_name}
                                        onChange={(companyName) => setFilters((prev) => ({ ...prev, company_name: companyName }))}
                                        onInputChange={setCompanyPartnerSearch}
                                        options={partnerCompanyOptions}
                                        loading={filterPartnersLoading}
                                        error={filterPartnersError}
                                        onKeyDown={handleKeyPress}
                                        placeholder="Rechercher un partenaire…"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Prénom client</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_first_name"
                                            value={filters.client_first_name || ''}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Prénom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom client</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_last_name"
                                            value={filters.client_last_name || ''}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Nom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email client</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="client_email"
                                            value={filters.client_email || ''}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Email du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date début (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_start_date_from"
                                            value={filters.activity_start_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date début (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_start_date_to"
                                            value={filters.activity_start_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_from"
                                            value={filters.activity_end_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_to"
                                            value={filters.activity_end_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            disabled={activities.loading}
                            className="d-flex align-items-center"
                        >
                            <FiRefreshCw className={`me-2 ${activities.loading ? 'spinning' : ''}`} />
                            Actualiser
                        </Button>
                    </div>

                    {activities.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des activités...</p>
                        </div>
                    ) : activities.error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{activities.error}</Alert>
                        </div>
                    ) : currentActivities.length === 0 ? (
                        <div className="text-center py-5">
                            <FiCalendar size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucune activité disponible</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <div className="table-scroll">
                                <Table striped hover className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Code activité</th>
                                            <th>Nom</th>
                                            <th>Catégorie</th>
                                            <th>Partenaire</th>
                                            <th>Date début</th>
                                            <th>Heure début</th>
                                            <th>Date fin</th>
                                            <th>Heure fin</th>
                                            <th>Ville</th>
                                            <th>Pays</th>
                                            <th>Prix</th>
                                            <th>Numéro de réservation</th>
                                            <th className="text-end col-actions">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(currentActivities) && currentActivities.length > 0 ? (
                                            currentActivities.map((activity) => {
                                                const activityId = activity.activity_id || activity.id;
                                                return (
                                                    <tr key={activityId}>
                                                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                            {getDeep(activity, 'activity_code', '-')}
                                                        </td>
                                                        <td>{getDeep(activity, 'name', 'N/A')}</td>
                                                        <td>
                                                            <Badge bg="info">{getDeep(activity, 'category', 'N/A')}</Badge>
                                                        </td>
                                                        <td>{getDeep(activity, ['partner_info', 'company_name'], 'N/A')}</td>
                                                        <td>{getDeep(activity, 'activity_start_date', 'N/A')}</td>
                                                        <td>{formatActivityTime(getDeep(activity, 'activity_start_time', null))}</td>
                                                        <td>{getDeep(activity, 'activity_end_date', 'N/A')}</td>
                                                        <td>{formatActivityTime(getDeep(activity, 'activity_end_time', null))}</td>
                                                        <td>{getDeep(activity, 'activity_city', 'N/A')}</td>
                                                        <td>{getDeep(activity, 'activity_country', 'N/A')}</td>
                                                        <td>{getDeep(activity, 'activity_price', 'N/A')}</td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                            {getDeep(activity, 'reservation_number', 'N/A')}
                                                        </td>
                                                        <td className="text-end col-actions">
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                className="me-2"
                                                                onClick={() => handleDetails(activityId)}
                                                                title="Détails"
                                                                aria-label="Détails"
                                                            >
                                                                <FiEye />
                                                            </Button>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="me-2"
                                                                onClick={() => handleEdit(activityId)}
                                                                title="Modifier"
                                                                aria-label="Modifier"
                                                            >
                                                                <FiEdit />
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleOpenDeleteModal(activity)}
                                                                title="Supprimer"
                                                                aria-label="Supprimer"
                                                            >
                                                                <FiTrash2 />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="13" className="text-center">
                                                    Aucune activité trouvée
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalItems > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div className="text-muted">
                                Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} activité{totalItems > 1 ? 's' : ''}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First
                                    onClick={() => {
                                        setCurrentPage(1);
                                        loadActivities(1);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => {
                                        const prevPage = currentPage - 1;
                                        setCurrentPage(prevPage);
                                        loadActivities(prevPage);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                {(() => {
                                    const { startPage, endPage } = getPaginationRange();
                                    const pages = [];

                                    // Show first page if not in range
                                    if (startPage > 1) {
                                        pages.push(
                                            <Pagination.Item key={1} onClick={() => {
                                                setCurrentPage(1);
                                                loadActivities(1);
                                            }}>
                                                1
                                            </Pagination.Item>
                                        );
                                        if (startPage > 2) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-start" />);
                                        }
                                    }

                                    // Show pages in range
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <Pagination.Item
                                                key={i}
                                                active={i === currentPage}
                                                onClick={() => {
                                                    setCurrentPage(i);
                                                    loadActivities(i);
                                                }}
                                            >
                                                {i}
                                            </Pagination.Item>
                                        );
                                    }

                                    // Show last page if not in range
                                    if (endPage < totalPages) {
                                        if (endPage < totalPages - 1) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                                        }
                                        pages.push(
                                            <Pagination.Item key={totalPages} onClick={() => {
                                                setCurrentPage(totalPages);
                                                loadActivities(totalPages);
                                            }}>
                                                {totalPages}
                                            </Pagination.Item>
                                        );
                                    }

                                    return pages;
                                })()}
                                <Pagination.Next
                                    onClick={() => {
                                        const nextPage = currentPage + 1;
                                        setCurrentPage(nextPage);
                                        loadActivities(nextPage);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => {
                                        setCurrentPage(totalPages);
                                        loadActivities(totalPages);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}

                    {/* Modal d'édition */}
                    <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>Modifier l'activité</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {updateActivity.error && (
                                <Alert variant="danger" className="mb-3">
                                    {updateActivity.error}
                                </Alert>
                            )}
                            <Form id="edit-activity-form" onSubmit={handleSubmitEdit}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Catégorie *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="category"
                                                value={formData.category}
                                                onChange={handleInputChange}
                                                required
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nom</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Prix *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="activity_price"
                                                value={formData.activity_price}
                                                onChange={handleInputChange}
                                                required
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Adresse *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="activity_address"
                                                value={formData.activity_address}
                                                onChange={handleInputChange}
                                                required
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Ville *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleInputChange}
                                                required
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Code postal</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="zip_code"
                                                value={formData.zip_code}
                                                onChange={handleInputChange}
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Pays *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleInputChange}
                                                required
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Date de début *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="activity_start_date"
                                                value={formData.activity_start_date}
                                                onChange={handleInputChange}
                                                required
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Heure de début *</Form.Label>
                                            <Form.Control
                                                type="time"
                                                name="activity_start_time"
                                                value={formData.activity_start_time}
                                                onChange={handleInputChange}
                                                required
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Date de fin *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="activity_end_date"
                                                value={formData.activity_end_date}
                                                onChange={handleInputChange}
                                                required
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Heure de fin *</Form.Label>
                                            <Form.Control
                                                type="time"
                                                name="activity_end_time"
                                                value={formData.activity_end_time}
                                                onChange={handleInputChange}
                                                required
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Numéro de réservation</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="reservation_number"
                                                value={formData.reservation_number}
                                                onChange={handleInputChange}
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nom du fournisseur</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="supplier_name"
                                                value={formData.supplier_name}
                                                onChange={handleInputChange}
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Adresse du fournisseur</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="supplier_address"
                                                value={formData.supplier_address}
                                                onChange={handleInputChange}
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Ville fournisseur</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="supplier_city"
                                                value={formData.supplier_city}
                                                onChange={handleInputChange}
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Code postal fournisseur</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="supplier_zip_code"
                                                value={formData.supplier_zip_code}
                                                onChange={handleInputChange}
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Pays fournisseur</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="supplier_country"
                                                value={formData.supplier_country}
                                                onChange={handleInputChange}
                                                disabled={updateActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={updateActivity.loading}>
                                Annuler
                            </Button>
                            <Button variant="primary" type="submit" form="edit-activity-form" disabled={updateActivity.loading}>
                                {updateActivity.loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Enregistrement...
                                    </>
                                ) : (
                                    'Enregistrer'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Modal de confirmation de suppression */}
                    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                        <Modal.Header closeButton>
                            <Modal.Title>Confirmer la suppression</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {deleteActivity.error && (
                                <Alert variant="danger" className="mb-3">
                                    {deleteActivity.error}
                                </Alert>
                            )}
                            Êtes-vous sûr de vouloir supprimer l'activité
                            <span className="fw-bold"> {activityToDelete?.activity_code || activityToDelete?.activity_id || 'cette activité'}</span> ?
                            Cette action est irréversible.
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={deleteActivity.loading}>
                                Annuler
                            </Button>
                            <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteActivity.loading}>
                                {deleteActivity.loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Suppression...
                                    </>
                                ) : (
                                    'Supprimer'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminActivities;
